import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { isBookingEligibleForReview, hasUserReviewedBooking } from '../../../services/reviewService';

const BookingCard = ({ booking, onViewDetails, onCancel, onWriteReview }) => {
  const [isEligibleForReview, setIsEligibleForReview] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [checkingReview, setCheckingReview] = useState(true);

  useEffect(() => {
    const checkReviewStatus = async () => {
      const eligible = isBookingEligibleForReview(booking);
      setIsEligibleForReview(eligible);

      if (eligible) {
        const reviewed = await hasUserReviewedBooking(booking?.bookingId || booking?.id);
        setHasReviewed(reviewed);
      }
      setCheckingReview(false);
    };

    checkReviewStatus();
  }, [booking]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-success text-success-foreground';
      case 'pending':
        return 'bg-warning text-warning-foreground';
      case 'cancelled':
        return 'bg-error text-error-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'text-success';
      case 'pending':
        return 'text-warning';
      case 'refunded':
        return 'text-muted-foreground';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-lg transition-all duration-250">
      <div className="flex flex-col md:flex-row">
        <div className="relative w-full md:w-48 lg:w-56 h-48 md:h-auto overflow-hidden flex-shrink-0">
          <Image
            src={booking?.image}
            alt={booking?.imageAlt}
            className="w-full h-full object-cover"
          />
          <div className="absolute top-3 left-3">
            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(booking?.status)}`}>
              {booking?.status?.charAt(0)?.toUpperCase() + booking?.status?.slice(1)}
            </span>
          </div>
        </div>

        <div className="flex-1 p-4 md:p-5 lg:p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
            <div className="flex-1">
              <h3 className="text-lg md:text-xl font-bold text-foreground mb-1">
                {booking?.name}
              </h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Icon name="MapPin" size={14} />
                <span>{booking?.location}</span>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2">
                {booking?.description}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground mb-1">Total Amount</p>
              <p className="text-xl md:text-2xl font-bold text-primary whitespace-nowrap">
                ₱{booking?.amount?.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className={`text-xs font-semibold mt-1 ${getPaymentStatusColor(booking?.paymentStatus)}`}>
                {booking?.paymentStatus === 'paid' && '✓ Paid'}
                {booking?.paymentStatus === 'pending' && 'Payment Pending'}
                {booking?.paymentStatus === 'refunded' && 'Refunded'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 pb-4 border-b border-border">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Calendar" size={16} color="var(--color-primary)" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Check-in</p>
                <p className="text-sm font-semibold text-foreground whitespace-nowrap">
                  {formatDate(booking?.checkIn)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Calendar" size={16} color="var(--color-secondary)" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Check-out</p>
                <p className="text-sm font-semibold text-foreground whitespace-nowrap">
                  {formatDate(booking?.checkOut)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Users" size={16} color="var(--color-accent)" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Guests</p>
                <p className="text-sm font-semibold text-foreground whitespace-nowrap">
                  {booking?.guests}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                <Icon name="Hash" size={16} />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Booking ID</p>
                <p className="text-sm font-semibold text-foreground whitespace-nowrap">
                  {booking?.bookingId}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={() => onViewDetails(booking?.id)}
              iconName="FileText"
              iconPosition="left"
            >
              View Confirmation
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
            >
              Download Receipt
            </Button>
            {isEligibleForReview && !hasReviewed && !checkingReview && (
              <Button
                variant="success"
                size="sm"
                onClick={() => onWriteReview?.(booking)}
                iconName="Star"
                iconPosition="left"
              >
                Write Review
              </Button>
            )}
            {hasReviewed && (
              <Button
                variant="ghost"
                size="sm"
                iconName="CheckCircle"
                iconPosition="left"
                disabled
              >
                Reviewed
              </Button>
            )}
            {booking?.status === 'confirmed' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onCancel(booking?.id)}
                iconName="XCircle"
                iconPosition="left"
              >
                Cancel Booking
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingCard;